// Sprint 7.3 — WelcomePage (One-Click-Login nach DOI).
// Flow: KT redirected nach DOI-Klick auf /willkommen?k={KT-Kontakt-Schlüssel}.
// Diese Seite POSTed an /api/auth/doi/handoff → Backend setzt Session-Cookie →
// User ist eingeloggt → wir zeigen 3 s einen Welcome-Moment + leiten dann ins Dashboard.
//
// Race-Condition-Handling: KT feuert erst Webhook → DANN Browser-Redirect. Der Webhook
// schreibt kt_subscriber_key auf den User. In seltenen Fällen (Netz-Jitter) kann der
// Browser-Redirect VOR dem Webhook ankommen → Retry-Loop mit Backoff.

import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Mail, AlertCircle, Send } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BRAND } from "@/config/brand";
// Sprint-V25 Meta-Pixel-Consolidation (Sa 12.09.2026): CompleteRegistration
// feuert nach erfolgreichem DOI-Handoff — User hat KT-DOI bestaetigt UND
// ist eingeloggt. Das ist der offizielle Confirm-Punkt der Registrierung.
// Hotfix Sa 12.09.2026 spät (Andreas-Direktive „Lead nur bei bestaetigtem
// DOI, nicht bei jeder Magic-Request"): Lead-Event zusaetzlich hier
// gefeuert, damit Meta korrekt auf echte Opt-Ins optimiert.
import { trackCompleteRegistration, trackLead } from "@/lib/metaPixel";
import { trackSignUp, trackGenerateLead, setUserId, getGaContext } from "@/lib/ga4";
import { CockpitWord } from "@/lib/journeyFlags";

const MAX_RETRIES = 5;
const RETRY_BACKOFF_MS = 1200;
const WELCOME_DISPLAY_MS = 3000;

export default function WelcomePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [phase, setPhase] = useState("loading"); // loading | welcome | fallback | error | resent
  const [firstName, setFirstName] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  // Sprint 14 Hotfix — Resend-Magic-Link Fallback wenn Auto-Login fehlschlägt
  const [resendEmail, setResendEmail] = useState("");
  const [resending, setResending] = useState(false);
  const [resendError, setResendError] = useState("");
  const k = params.get("k") || "";
  const startedRef = useRef(false);

  useEffect(() => {
    // Sprint 7.3 — Guard gegen React-18-StrictMode-Doppellauf (mount → unmount → remount).
    // Modul-level Ref + Backend-side Single-Use-Invalidierung schützen vor Doppel-Konsum
    // des KT-Keys. Setstate nach Unmount ist in React 18 erlaubt (kein Warning mehr).
    if (startedRef.current) return;
    startedRef.current = true;

    if (!k) {
      setPhase("fallback");
      setErrorMsg("Wir konnten dich nicht automatisch einloggen. Schau in dein Postfach — dort wartet dein Login-Link.");
      return;
    }

    const attemptHandoff = async () => {
      // Sprint V28 (Fr 27.02.2026) — GA4-Context (client_id/session_id) VOR
      // dem doi/handoff-Call ans Backend uebergeben, damit die Server-Side
      // GA4-Events (sign_up + generate_lead, gefeuert im Handoff-Handler)
      // an den richtigen Browser-Session-Kontext andocken.
      //
      // Sprint V103.b (05.10.2026, Loch-A-Fix) — Der frühere separate
      // POST /analytics/ga4-context lief VOR dem Cookie-Issue → 401 →
      // silent swallow → `ga4_client_id` war in 100 % der Fälle leer. Jetzt
      // reichen wir den Context direkt im Handoff-Body durch; das Backend
      // persistiert ihn transaktional vor dem Dispatch der Server-GA4-Events.
      let ga4Ctx = {};
      try {
        ga4Ctx = (await getGaContext()) || {};
      } catch (_e) { /* silent — analytics-only */ }

      for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
        try {
          const { data } = await api.post("/auth/doi/handoff", {
            k,
            // Nur mitschicken, wenn gtag Werte geliefert hat. Backend
            // ignoriert leeres Dict.
            ga4: (ga4Ctx.client_id || ga4Ctx.session_id) ? ga4Ctx : undefined,
          });
          setFirstName(data?.first_name || "");
          await refresh();
          // Sprint-V25 Meta-Pixel: Lead + CompleteRegistration feuern beide
          // hier — dieser Punkt (/willkommen) ist die einzige Stelle, an der
          // ein Opt-In wirklich abgeschlossen ist (KT-DOI bestaetigt +
          // Login erfolgt). Meta optimiert damit auf echte Confirmed-Leads.
          const uid = data?.user_id || data?.id || null;
          // Sprint V26 (Fr 27.02.2026) — Meta CAPI Dedup: das Backend liefert
          // pro User genau eine event_id fuer Lead und CompleteRegistration.
          // Diese `event_id` MUSS als `{eventID: <id>}` an fbq uebergeben
          // werden, damit Meta Browser-Pixel- und Server-CAPI-Events mergt
          // (Fenster: 48h). Fallback: intern generierte Idempotenz-Keys.
          const capiEventIds = data?.capi_event_ids || {};
          trackLead({
            source: "doi_confirmed", userId: uid,
            eventId: capiEventIds.lead || null,
          });
          trackCompleteRegistration({
            userId: uid, method: "magic_link_doi",
            eventId: capiEventIds.complete_registration || null,
          });
          // Sprint V27 (Fr 27.02.2026) — GA4-Events spiegeln Meta-Pixel:
          // sign_up (= CompleteRegistration) + generate_lead (= Lead).
          // Zusaetzlich User-ID setzen fuer Cross-Device-Tracking.
          if (uid) setUserId(uid);
          const fokustyp = data?.fokustyp_primary || null;
          trackSignUp({ userId: uid, method: "magic_link_doi" });
          trackGenerateLead({ userId: uid, fokustyp, source: "doi_confirmed" });
          setPhase("welcome");
          setTimeout(() => {
            // Sprint 8.A.1 — Post-Login Redirect zu /cockpit statt /dashboard
            navigate(data?.post_doi_path || "/cockpit", { replace: true });
          }, WELCOME_DISPLAY_MS);
          return;
        } catch (err) {
          const status = err?.response?.status;
          const code = err?.response?.data?.detail?.code;
          // 410 = expired/not_confirmed → kein Retry, Fallback.
          if (status === 410 || code === "expired" || code === "not_confirmed") {
            setPhase("fallback");
            setErrorMsg("Dein Login-Fenster ist abgelaufen. Wir haben dir den Magic-Link in dein Postfach geschickt — klick einfach drauf.");
            return;
          }
          // 404 = key_not_found → Race-Condition (KT-Webhook noch nicht durch), retry.
          if (attempt < MAX_RETRIES - 1) {
            await new Promise((r) => setTimeout(r, RETRY_BACKOFF_MS));
          } else {
            setPhase("fallback");
            setErrorMsg("Wir konnten dich nicht automatisch einloggen. Schau in dein Postfach — dort wartet dein Login-Link.");
          }
        }
      }
    };

    attemptHandoff();
    // Kein Cleanup nötig — startedRef + Backend-Replay-Schutz reichen aus.
  }, [k, navigate, refresh]);

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-xl border border-slate-100 p-8 sm:p-10 text-center">
        {/* F4S Brand-Mark */}
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-black text-white text-3xl font-black mx-auto mb-3" data-testid="welcome-brand-mark">
          4
        </div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-8">
          {BRAND.name}
        </p>

        {phase === "loading" && (
          <div data-testid="welcome-loading">
            <Loader2 className="w-8 h-8 text-slate-400 animate-spin mx-auto" />
            <h1 className="mt-6 text-xl font-extrabold text-slate-900">
              Wir melden dich an…
            </h1>
            <p className="mt-2 text-sm text-slate-500 leading-relaxed">
              Einen Moment, wir richten deinen PocketCoach her.
            </p>
          </div>
        )}

        {phase === "welcome" && (
          <div data-testid="welcome-success">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight">
              Willkommen{firstName ? `, ${firstName}` : ""}!
            </h1>
            <p className="mt-3 text-sm text-slate-600 leading-relaxed">
              Du bist eingeloggt. Dein PocketCoach öffnet sich gleich automatisch — da wartet alles auf dich, um deine Botschaften landen zu lassen.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 text-xs font-semibold text-slate-400">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Weiter zum PocketCoach
            </div>
          </div>
        )}

        {phase === "fallback" && (
          <div data-testid="welcome-fallback">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-50 text-amber-600 mx-auto mb-4">
              <Mail className="w-7 h-7" />
            </div>
            <h1 className="text-xl font-extrabold text-slate-900">
              Auto-Login hat nicht geklappt
            </h1>
            <p className="mt-3 text-sm text-slate-600 leading-relaxed">
              Kein Problem — wir schicken dir einen frischen Login-Link.
              Trag bitte deine E-Mail-Adresse ein, mit der du dich registriert hast:
            </p>

            <form
              className="mt-5 space-y-3"
              onSubmit={async (e) => {
                e.preventDefault();
                setResendError("");
                const email = resendEmail.trim().toLowerCase();
                if (!email || !email.includes("@")) {
                  setResendError("Bitte gültige E-Mail-Adresse eingeben.");
                  return;
                }
                setResending(true);
                try {
                  await api.post("/auth/magic/request", { email });
                  setPhase("resent");
                } catch (err) {
                  const status = err?.response?.status;
                  if (status === 429) {
                    setResendError("Zu viele Anfragen. Bitte in 1 Minute erneut versuchen.");
                  } else {
                    setResendError("Konnten den Link nicht senden. Bitte erneut versuchen.");
                  }
                } finally {
                  setResending(false);
                }
              }}
            >
              <input
                type="email"
                required
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                placeholder="du@beispiel.de"
                data-testid="welcome-fallback-email"
                className="w-full h-11 px-4 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#4563BB]"
              />
              {resendError && (
                <p data-testid="welcome-fallback-error" className="text-xs text-red-600 text-left">
                  {resendError}
                </p>
              )}
              <button
                type="submit"
                disabled={resending || !resendEmail}
                data-testid="welcome-fallback-send-btn"
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-[#4563BB] text-white text-sm font-bold hover:bg-[#3a55a3] disabled:opacity-50"
              >
                {resending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Wird gesendet…
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" /> Magic-Link an mich senden
                  </>
                )}
              </button>
            </form>

            <button
              type="button"
              onClick={() => navigate("/")}
              data-testid="welcome-fallback-home"
              className="mt-4 text-xs text-slate-500 hover:text-slate-900 font-semibold underline underline-offset-2"
            >
              Zur Startseite
            </button>
          </div>
        )}

        {phase === "resent" && (
          <div data-testid="welcome-resent">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <h1 className="text-xl font-extrabold text-slate-900">
              Check dein Postfach.
            </h1>
            <p className="mt-3 text-sm text-slate-600 leading-relaxed">
              Wir haben dir gerade einen frischen Login-Link an{" "}
              <span className="font-semibold text-slate-900">{resendEmail}</span>{" "}
              geschickt. Klick einfach drauf — du bist sofort drin.
            </p>
            <p className="mt-4 text-xs text-slate-500">
              Nichts im Postfach? Schau auch im Spam-Ordner nach.
            </p>
          </div>
        )}

        {phase === "error" && (
          <div data-testid="welcome-error">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-50 text-red-600 mx-auto mb-4">
              <AlertCircle className="w-7 h-7" />
            </div>
            <h1 className="text-xl font-extrabold text-slate-900">
              Hoppla
            </h1>
            <p className="mt-3 text-sm text-slate-600">
              {errorMsg || "Etwas ist schiefgelaufen. Bitte versuche es gleich nochmal."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
